import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  Activity,
  Bell,
  Cpu,
  Database,
  Download,
  Gauge,
  GitBranch,
  HardDrive,
  Layers,
  MemoryStick,
  Pause,
  Play,
  Radio,
  Search,
  Thermometer,
  Trash2,
  Wifi
} from "lucide-react";
import { clamp, formatBytes, formatTime, formatUptime, percentTone, serviceRank, severityLabel, statusLabel } from "./format";
import { useStatusStream } from "./useStatusStream";
import { HorizontalMeter, RingGauge, Sparkline, TopologyMap } from "./visuals";
import type { EventSeverity, MetricSnapshot, QueueStatus, ServiceState, ServiceStatus, StreamState } from "./types";

type WindowKey = "1m" | "5m" | "15m";
type ServiceSort = "status" | "name" | "latency";
type FeedFilter = "all" | EventSeverity;

const windowMs: Record<WindowKey, number> = {
  "1m": 60_000,
  "5m": 300_000,
  "15m": 900_000
};

function classNames(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function streamCopy(state: StreamState) {
  if (state === "live") return "Live";
  if (state === "paused") return "Paused";
  if (state === "error") return "Reconnecting";
  return "Connecting";
}

function streamTone(state: StreamState) {
  if (state === "live") return "good";
  if (state === "paused") return "warn";
  if (state === "error") return "critical";
  return "muted";
}

function overallTone(snapshot: MetricSnapshot | null) {
  if (!snapshot) return "muted";
  const serviceDown = snapshot.services.some((service) => service.status === "down");
  const serviceWarn = snapshot.services.some((service) => service.status === "warn");
  const daemonDown = snapshot.daemons.some((daemon) => daemon.state === "down") || snapshot.thermodynamic.state === "down";
  const daemonWarn = snapshot.daemons.some((daemon) => daemon.state === "warn") || snapshot.thermodynamic.state === "warn";
  const hotGpu = Boolean(snapshot.gpu.available && snapshot.gpu.temp != null && snapshot.gpu.temp >= 80);
  if (daemonDown || serviceDown || hotGpu || snapshot.cpu.usage >= 90 || snapshot.memory.percent >= 92) return "critical";
  if (daemonWarn || serviceWarn || snapshot.cpu.usage >= 75 || snapshot.memory.percent >= 80) return "warn";
  return "good";
}

function stateTone(state: ServiceState | null | undefined) {
  if (state === "ok") return "good";
  if (state === "warn") return "warn";
  if (state === "down") return "critical";
  return "muted";
}

function formatNumber(value: number | null | undefined, maximumFractionDigits = 0) {
  if (value == null || !Number.isFinite(value)) return "--";
  return new Intl.NumberFormat(undefined, { maximumFractionDigits }).format(value);
}

function formatAge(seconds: number | null | undefined) {
  if (seconds == null) return "--";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  return formatUptime(seconds);
}

function categoryLabel(category: ServiceStatus["category"]) {
  if (category === "edge") return "Edge";
  if (category === "knowledge") return "Knowledge";
  if (category === "resource") return "Resource";
  if (category === "daemon") return "Daemon";
  return "Integration";
}

function StatusDot({ tone, label }: { tone: string; label: string }) {
  return (
    <span className="status-dot-wrap">
      <span className="status-dot" data-tone={tone} />
      {label}
    </span>
  );
}

function IconButton({
  label,
  active,
  children,
  onClick
}: {
  label: string;
  active?: boolean;
  children: ReactNode;
  onClick?: () => void;
}) {
  return (
    <button className={classNames("icon-button", active && "is-active")} type="button" title={label} aria-label={label} onClick={onClick}>
      {children}
    </button>
  );
}

function Segmented<T extends string>({
  value,
  options,
  onChange
}: {
  value: T;
  options: T[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="segmented" role="tablist">
      {options.map((option) => (
        <button
          key={option}
          className={option === value ? "is-selected" : ""}
          type="button"
          role="tab"
          aria-selected={option === value}
          onClick={() => onChange(option)}
        >
          {option}
        </button>
      ))}
    </div>
  );
}

function MetricPanel({
  icon,
  label,
  value,
  detail,
  sparkValues,
  tone
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  detail: string;
  sparkValues: number[];
  tone: "good" | "warn" | "critical" | "cyan" | "muted";
}) {
  return (
    <section className="metric-panel" data-tone={tone}>
      <div className="metric-head">
        <span className="metric-icon">{icon}</span>
        <span>{label}</span>
      </div>
      <strong>{value}</strong>
      <span className="metric-detail">{detail}</span>
      <Sparkline values={sparkValues} tone={tone} height={42} max={label === "Network" ? undefined : 100} />
    </section>
  );
}

function ThermodynamicPanel({ snapshot }: { snapshot: MetricSnapshot | null }) {
  const thermo = snapshot?.thermodynamic;
  const tone = stateTone(thermo?.state);
  const daemon = snapshot?.daemons.find((item) => item.name === "Yennefer thermodynamic daemon");

  return (
    <section className="panel daemon-panel" data-tone={tone}>
      <div className="panel-heading">
        <div>
          <h2>Thermodynamic Daemon</h2>
          <span>{thermo?.source ?? "Awaiting soul state"}</span>
        </div>
        <StatusDot tone={tone} label={thermo?.state === "ok" ? "Online" : thermo?.state === "warn" ? "Stale" : "Down"} />
      </div>
      <div className="daemon-core">
        <div className="daemon-yield">
          <span>Yield</span>
          <strong>{formatNumber(thermo?.thermodynamicYield, 2)}</strong>
          <small>{formatNumber(thermo?.tokensGeneratedPerSec, 2)} tokens/sec</small>
        </div>
        <div className="daemon-metrics">
          <div>
            <span>Coherence</span>
            <strong>{thermo?.coherencePercent == null ? "--" : `${formatNumber(thermo.coherencePercent, 1)}%`}</strong>
          </div>
          <div>
            <span>Breath</span>
            <strong>{formatNumber(thermo?.breath, 2)}</strong>
          </div>
          <div>
            <span>Freshness</span>
            <strong>{formatAge(thermo?.ageSeconds)}</strong>
          </div>
          <div>
            <span>PID</span>
            <strong>{daemon?.pid ?? "--"}</strong>
          </div>
        </div>
      </div>
      <div className="daemon-state-line">
        <span>{thermo?.protocol ?? "YENNEFER"}</span>
        <span>{thermo?.concaveState ?? "--"}</span>
        <span>{thermo?.derivativeState ?? "--"}</span>
        <span>uptime {formatAge(thermo?.uptimeSeconds)}</span>
      </div>
    </section>
  );
}

function QueuePanel({ queue }: { queue: QueueStatus | null | undefined }) {
  const backlog = queue?.inbox ?? 0;
  const tone = backlog > 0 ? "warn" : "good";

  return (
    <section className="panel queue-panel" data-tone={tone}>
      <div className="panel-heading">
        <div>
          <h2>Handoff Queue</h2>
          <span>{queue?.logUpdatedAt ? `log ${formatTime(queue.logUpdatedAt)}` : "No daemon log"}</span>
        </div>
        <GitBranch size={18} />
      </div>
      <div className="queue-grid">
        <div>
          <span>Inbox</span>
          <strong>{queue?.inbox ?? 0}</strong>
        </div>
        <div>
          <span>Materialized</span>
          <strong>{queue?.materialized ?? 0}</strong>
        </div>
        <div>
          <span>Executed</span>
          <strong>{queue?.executed ?? 0}</strong>
        </div>
        <div>
          <span>Completed</span>
          <strong>{queue?.completed ?? 0}</strong>
        </div>
      </div>
      <p className="queue-log">{queue?.lastLogLine ?? "No recent handoff activity"}</p>
    </section>
  );
}

function DaemonList({ snapshot }: { snapshot: MetricSnapshot | null }) {
  return (
    <section className="panel daemon-list-panel">
      <div className="panel-heading">
        <div>
          <h2>Daemon Fleet</h2>
          <span>{snapshot?.daemons.length ?? 0} runtime probes</span>
        </div>
        <Layers size={18} />
      </div>
      <div className="daemon-list">
        {(snapshot?.daemons ?? []).map((daemon) => (
          <div className="daemon-row" data-tone={stateTone(daemon.state)} key={daemon.name}>
            <StatusDot tone={stateTone(daemon.state)} label={daemon.state === "ok" ? "Online" : daemon.state === "warn" ? "Stale" : "Down"} />
            <strong>{daemon.name}</strong>
            <span>{daemon.pid == null ? daemon.detail : `pid ${daemon.pid} · ${formatAge(daemon.uptimeSeconds)}`}</span>
            <span>{daemon.cpu == null ? "--" : `${daemon.cpu.toFixed(1)}% cpu`}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function ServiceTable({
  services,
  selected,
  onSelect
}: {
  services: ServiceStatus[];
  selected: string | null;
  onSelect: (name: string) => void;
}) {
  const [sort, setSort] = useState<ServiceSort>("status");
  const sortedServices = useMemo(() => {
    return [...services].sort((a, b) => {
      if (sort === "name") return a.name.localeCompare(b.name);
      if (sort === "latency") return (a.latency ?? 99_999) - (b.latency ?? 99_999);
      return serviceRank(a.status) - serviceRank(b.status);
    });
  }, [services, sort]);

  return (
    <section className="panel services-panel" id="services-panel">
      <div className="panel-heading">
        <div>
          <h2>Connected Resources</h2>
          <span>{services.length} integration checks</span>
        </div>
        <Segmented<ServiceSort> value={sort} options={["status", "name", "latency"]} onChange={setSort} />
      </div>
      <div className="service-table">
        <div className="service-row service-row-head">
          <span>Name</span>
          <span>Type</span>
          <span>Status</span>
          <span>Latency</span>
          <span>Scope</span>
        </div>
        {sortedServices.map((service) => (
          <button
            key={service.name}
            className={classNames("service-row", selected === service.name && "is-selected")}
            type="button"
            onClick={() => onSelect(service.name)}
          >
            <span className="service-name">{service.name}</span>
            <span>{categoryLabel(service.category)}</span>
            <span className="service-status" data-state={service.status}>
              {statusLabel(service.status)}
            </span>
            <span>{service.latency == null ? "--" : `${service.latency} ms`}</span>
            <span>{service.local ? "Local" : "Remote"}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function EventFeed({
  snapshot,
  clearedThrough,
  onClear
}: {
  snapshot: MetricSnapshot | null;
  clearedThrough: number;
  onClear: () => void;
}) {
  const [filter, setFilter] = useState<FeedFilter>("all");
  const [query, setQuery] = useState("");
  const events = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (snapshot?.events ?? [])
      .filter((event) => event.id > clearedThrough)
      .filter((event) => filter === "all" || event.severity === filter)
      .filter((event) => {
        if (!needle) return true;
        return `${event.source} ${event.message} ${event.kind}`.toLowerCase().includes(needle);
      });
  }, [snapshot, clearedThrough, filter, query]);

  return (
    <aside className="panel feed-panel" id="feed-panel">
      <div className="panel-heading">
        <div>
          <h2>Live Feed</h2>
          <span>{events.length} visible</span>
        </div>
        <IconButton label="Clear" onClick={onClear}>
          <Trash2 size={17} />
        </IconButton>
      </div>
      <div className="feed-tools">
        <label className="search-box">
          <Search size={15} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Filter" />
        </label>
        <Segmented<FeedFilter> value={filter} options={["all", "info", "warn", "critical"]} onChange={setFilter} />
      </div>
      <div className="event-list">
        {events.length === 0 ? (
          <div className="empty-feed">No matching events</div>
        ) : (
          events.map((event) => (
            <article className="event-row" data-severity={event.severity} key={event.id}>
              <div className="event-meta">
                <span>{formatTime(event.timestamp)}</span>
                <span>{severityLabel(event.severity)}</span>
              </div>
              <strong>{event.source}</strong>
              <p>{event.message}</p>
            </article>
          ))
        )}
      </div>
    </aside>
  );
}

function ProcessTable({ snapshot }: { snapshot: MetricSnapshot | null }) {
  return (
    <section className="panel process-panel" id="processes-panel">
      <div className="panel-heading">
        <div>
          <h2>Processes</h2>
          <span>Top CPU consumers</span>
        </div>
      </div>
      <div className="process-list">
        {(snapshot?.processes ?? []).map((process) => (
          <div className="process-row" key={`${process.pid}-${process.name}`}>
            <span>{process.name}</span>
            <span>#{process.pid}</span>
            <HorizontalMeter label="CPU" value={clamp(process.cpu)} tone={percentTone(process.cpu)} detail={`${process.cpu.toFixed(1)}%`} />
            <span>{process.memory.toFixed(1)}% mem</span>
          </div>
        ))}
      </div>
    </section>
  );
}

export function App() {
  const [paused, setPaused] = useState(false);
  const [windowKey, setWindowKey] = useState<WindowKey>("5m");
  const [selectedNode, setSelectedNode] = useState<string | null>("CPU");
  const [clearedThrough, setClearedThrough] = useState(0);
  const { snapshot, history, streamState } = useStatusStream(paused);
  const tone = overallTone(snapshot);

  const visibleHistory = useMemo(() => {
    const floor = Date.now() - windowMs[windowKey];
    return history.filter((item) => Date.parse(item.timestamp) >= floor);
  }, [history, windowKey]);

  const cpuValues = visibleHistory.map((item) => item.cpu.usage);
  const memoryValues = visibleHistory.map((item) => item.memory.percent);
  const gpuValues = visibleHistory.map((item) => item.gpu.memoryPercent ?? 0);
  const tempValues = visibleHistory.map((item) => item.gpu.temp ?? 0);
  const rxValues = visibleHistory.map((item) => item.network.rxBps);
  const txValues = visibleHistory.map((item) => item.network.txBps);
  const netMax = Math.max(...rxValues, ...txValues, 1);
  const primaryDisk = snapshot?.disk[0];

  function exportStatus() {
    const payload = JSON.stringify(
      {
        exportedAt: new Date().toISOString(),
        snapshot,
        history: visibleHistory
      },
      null,
      2
    );
    const url = URL.createObjectURL(new Blob([payload], { type: "application/json" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `diamondnode-status-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="app-shell">
      <aside className="nav-rail" aria-label="Dashboard sections">
        <div className="rail-mark">
          <Radio size={22} />
        </div>
        <button className="is-active" type="button" title="Overview" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          <Activity size={20} />
        </button>
        <button type="button" title="Services" onClick={() => document.getElementById("services-panel")?.scrollIntoView({ behavior: "smooth" })}>
          <Database size={20} />
        </button>
        <button type="button" title="Processes" onClick={() => document.getElementById("processes-panel")?.scrollIntoView({ behavior: "smooth" })}>
          <Cpu size={20} />
        </button>
        <button type="button" title="Live Feed" onClick={() => document.getElementById("feed-panel")?.scrollIntoView({ behavior: "smooth" })}>
          <Bell size={20} />
        </button>
      </aside>

      <main className="status-surface">
        <header className="topbar">
          <div className="title-block">
            <h1>Thermodynamic Daemon</h1>
            <div className="host-line">
              <StatusDot tone={streamTone(streamState)} label={streamCopy(streamState)} />
              <StatusDot tone={tone} label={tone === "good" ? "Nominal" : tone === "warn" ? "Degraded" : "Incident"} />
              <span>{snapshot?.host ?? "loading"}</span>
              <span>{snapshot ? formatTime(snapshot.timestamp) : "--"}</span>
            </div>
          </div>
          <div className="topbar-controls">
            <Segmented<WindowKey> value={windowKey} options={["1m", "5m", "15m"]} onChange={setWindowKey} />
            <button className="control-button" type="button" onClick={() => setPaused((value) => !value)}>
              {paused ? <Play size={16} /> : <Pause size={16} />}
              {paused ? "Resume" : "Pause"}
            </button>
            <button className="control-button" type="button" onClick={exportStatus}>
              <Download size={16} />
              Export
            </button>
          </div>
        </header>

        <div className="dashboard-grid">
          <section className="metric-strip" aria-label="Core metrics">
            <MetricPanel
              icon={<Cpu size={17} />}
              label="CPU"
              value={`${Math.round(snapshot?.cpu.usage ?? 0)}%`}
              detail={`${snapshot?.cpu.cores ?? 0} cores`}
              sparkValues={cpuValues}
              tone={percentTone(snapshot?.cpu.usage)}
            />
            <MetricPanel
              icon={<MemoryStick size={17} />}
              label="Memory"
              value={`${Math.round(snapshot?.memory.percent ?? 0)}%`}
              detail={snapshot ? `${snapshot.memory.usedText} / ${snapshot.memory.totalText}` : "--"}
              sparkValues={memoryValues}
              tone={percentTone(snapshot?.memory.percent)}
            />
            <MetricPanel
              icon={<Gauge size={17} />}
              label="GPU VRAM"
              value={snapshot?.gpu.available ? `${Math.round(snapshot.gpu.memoryPercent ?? 0)}%` : "--"}
              detail={
                snapshot?.gpu.available
                  ? `${snapshot.gpu.memoryUsed ?? 0} / ${snapshot.gpu.memoryTotal ?? 0} MiB`
                  : "not available"
              }
              sparkValues={gpuValues}
              tone={snapshot?.gpu.available ? percentTone(snapshot.gpu.memoryPercent) : "muted"}
            />
            <MetricPanel
              icon={<Wifi size={17} />}
              label="Network"
              value={snapshot?.network.rxText ?? "0 B/s"}
              detail={`tx ${snapshot?.network.txText ?? "0 B/s"}`}
              sparkValues={rxValues}
              tone="cyan"
            />
          </section>

          <ThermodynamicPanel snapshot={snapshot} />
          <QueuePanel queue={snapshot?.queue} />

          <section className="panel gauge-panel">
            <div className="panel-heading">
              <div>
                <h2>Telemetry</h2>
                <span>{snapshot ? `${snapshot.platform} ${snapshot.arch}` : "Awaiting stream"}</span>
              </div>
              <span className="uptime">Uptime {snapshot ? formatUptime(snapshot.uptime) : "--"}</span>
            </div>
            <div className="gauge-grid">
              <RingGauge label="CPU" value={snapshot?.cpu.usage} tone={percentTone(snapshot?.cpu.usage)} />
              <RingGauge label="Memory" value={snapshot?.memory.percent} tone={percentTone(snapshot?.memory.percent)} />
              <RingGauge
                label="GPU Temp"
                value={snapshot?.gpu.temp}
                unit="C"
                tone={snapshot?.gpu.available ? percentTone(snapshot.gpu.temp) : "muted"}
              />
              <RingGauge label="GPU VRAM" value={snapshot?.gpu.memoryPercent} tone={snapshot?.gpu.available ? "cyan" : "muted"} />
            </div>
          </section>

          <section className="panel history-panel">
            <div className="panel-heading">
              <div>
                <h2>Load Graph</h2>
                <span>{windowKey} rolling window</span>
              </div>
            </div>
            <div className="chart-stack">
              <div className="chart-row">
                <div>
                  <strong>CPU</strong>
                  <span>{Math.round(snapshot?.cpu.usage ?? 0)}%</span>
                </div>
                <Sparkline values={cpuValues} tone={percentTone(snapshot?.cpu.usage)} max={100} />
              </div>
              <div className="chart-row">
                <div>
                  <strong>Memory</strong>
                  <span>{Math.round(snapshot?.memory.percent ?? 0)}%</span>
                </div>
                <Sparkline values={memoryValues} tone={percentTone(snapshot?.memory.percent)} max={100} />
              </div>
              <div className="chart-row">
                <div>
                  <strong>GPU Temp</strong>
                  <span>{snapshot?.gpu.temp ?? "--"}C</span>
                </div>
                <Sparkline values={tempValues} tone={percentTone(snapshot?.gpu.temp)} max={100} />
              </div>
            </div>
          </section>

          <section className="panel topology-panel">
            <div className="panel-heading">
              <div>
                <h2>Topology</h2>
                <span>{selectedNode ?? "Auto"}</span>
              </div>
            </div>
            <TopologyMap snapshot={snapshot} selected={selectedNode} onSelect={setSelectedNode} />
          </section>

          <section className="panel network-panel">
            <div className="panel-heading">
              <div>
                <h2>Network</h2>
                <span>RX/TX throughput</span>
              </div>
            </div>
            <div className="network-charts">
              <div>
                <span>RX {snapshot?.network.rxText ?? "0 B/s"}</span>
                <Sparkline values={rxValues} tone="cyan" max={netMax} />
              </div>
              <div>
                <span>TX {snapshot?.network.txText ?? "0 B/s"}</span>
                <Sparkline values={txValues} tone="good" max={netMax} />
              </div>
            </div>
          </section>

          <section className="panel disk-panel">
            <div className="panel-heading">
              <div>
                <h2>Disk</h2>
                <span>{primaryDisk?.filesystem ?? "--"}</span>
              </div>
              <HardDrive size={18} />
            </div>
            <div className="disk-list">
              {(snapshot?.disk ?? []).map((disk) => (
                <HorizontalMeter
                  key={`${disk.filesystem}-${disk.mount}`}
                  label={disk.label}
                  value={disk.percent}
                  tone={percentTone(disk.percent)}
                  detail={`${disk.percent}% · ${formatBytes(disk.used)} used`}
                />
              ))}
            </div>
          </section>

          <section className="panel thermal-panel">
            <div className="panel-heading">
              <div>
                <h2>Thermal</h2>
                <span>{snapshot?.gpu.name ?? "GPU"}</span>
              </div>
              <Thermometer size={18} />
            </div>
            <div className="thermal-band">
              <span style={{ left: `${clamp(snapshot?.gpu.temp ?? 0)}%` }} />
            </div>
            <div className="thermal-copy">
              <strong>{snapshot?.gpu.temp == null ? "--" : `${snapshot.gpu.temp}C`}</strong>
              <span>{snapshot?.gpu.powerDraw == null ? "--" : `${snapshot.gpu.powerDraw}W / ${snapshot.gpu.powerLimit}W`}</span>
            </div>
          </section>

          <ServiceTable services={snapshot?.services ?? []} selected={selectedNode} onSelect={setSelectedNode} />
          <DaemonList snapshot={snapshot} />
          <ProcessTable snapshot={snapshot} />
          <EventFeed
            snapshot={snapshot}
            clearedThrough={clearedThrough}
            onClear={() => setClearedThrough(snapshot?.events[0]?.id ?? clearedThrough)}
          />
        </div>
      </main>
    </div>
  );
}
